import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import { Dialog, DialogContent } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { ROUTES } from '@/routes';

import { useMembershipPreviewMode } from './MembershipContentLock';

export type MembershipProductOptimizationPreviewTutorialAction =
  | 'startDiagnosis'
  | 'deleteDiagnosis';

type TutorialCopy = {
  title: string;
  description: string;
  result: string;
};

type MembershipProductOptimizationPreviewTutorialContextValue = {
  openTutorial: (action: MembershipProductOptimizationPreviewTutorialAction) => boolean;
};

const tutorialCopyByAction = {
  startDiagnosis: {
    title: '상품 최적화 진단 시작 체험',
    description:
      '스마트스토어 상품 URL을 입력해 상품명, 카테고리, 태그, 판매 관리 항목을 진단하는 흐름이에요.',
    result: '구독 후에는 실제 상품을 진단하고 항목별 개선 포인트와 최적화 점수를 확인할 수 있어요.',
  },
  deleteDiagnosis: {
    title: '진단 상품 삭제 체험',
    description: '더 이상 확인하지 않을 상품 진단 결과를 목록에서 정리하는 흐름이에요.',
    result: '구독 후에는 실제 진단 상품을 삭제하고 월 진단 목록을 관리할 수 있어요.',
  },
} satisfies Record<MembershipProductOptimizationPreviewTutorialAction, TutorialCopy>;

const MembershipProductOptimizationPreviewTutorialContext =
  createContext<MembershipProductOptimizationPreviewTutorialContextValue | null>(null);

export const MembershipProductOptimizationPreviewTutorialProvider = ({
  children,
}: PropsWithChildren) => {
  const isPreviewMode = useMembershipPreviewMode();
  const navigate = useNavigate();
  const [isTutorialOpen, setIsTutorialOpen] = useState(false);
  const [activeAction, setActiveAction] =
    useState<MembershipProductOptimizationPreviewTutorialAction | null>(null);

  const closeTutorial = () => setIsTutorialOpen(false);

  const openTutorial = useCallback(
    (action: MembershipProductOptimizationPreviewTutorialAction) => {
      if (!isPreviewMode) return false;

      setActiveAction(action);
      setIsTutorialOpen(true);
      return true;
    },
    [isPreviewMode],
  );

  const contextValue = useMemo(() => ({ openTutorial }), [openTutorial]);
  const activeCopy = activeAction ? tutorialCopyByAction[activeAction] : null;

  return (
    <MembershipProductOptimizationPreviewTutorialContext.Provider value={contextValue}>
      {children}
      <Dialog open={isTutorialOpen} onOpenChange={(open) => !open && closeTutorial()}>
        <DialogContent
          noCloseBtn
          className="w-[500px] max-w-[calc(100vw-32px)] p-[30px]"
          data-membership-preview-interactive="true"
        >
          {activeCopy && (
            <div className="flex flex-col gap-5">
              <div className="flex flex-col gap-1">
                <span className="text-point-500 Caption1M12">상품 최적화 진단 체험</span>
                <div className="text-gr-900 Title3S18">{activeCopy.title}</div>
                <div className="text-gr-700 Body5M14">{activeCopy.description}</div>
              </div>
              <div className="rounded-[8px] bg-gr-50 p-4 text-gr-800 Body5M14">
                {activeCopy.result}
              </div>
              <div className="flex gap-3">
                <Button className="h-[44px] flex-1" variant="secondary" onClick={closeTutorial}>
                  계속 둘러보기
                </Button>
                <Button
                  className="h-[44px] flex-1"
                  variant="solid"
                  onClick={() => navigate(ROUTES.moreMembership)}
                >
                  구독하고 사용하기
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </MembershipProductOptimizationPreviewTutorialContext.Provider>
  );
};

export const useMembershipProductOptimizationPreviewTutorial = () => {
  const context = useContext(MembershipProductOptimizationPreviewTutorialContext);
  if (!context) {
    throw new Error(
      'useMembershipProductOptimizationPreviewTutorial must be used within MembershipProductOptimizationPreviewTutorialProvider',
    );
  }

  return context;
};
